import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import './PaymentSuccess.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { fetchUserProfile } = useContext(AuthContext);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // PayHere doesn't pass parameters to return_url, so get order_id from localStorage
        const orderId = localStorage.getItem('payhere_order_id');
        
        if (!orderId) {
          setError('Order ID not found. Please check your payment status in the dashboard.');
          setLoading(false);
          return;
        }

        // Fetch payment status from backend (database was updated by notify_url)
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/payment/status/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPaymentStatus(response.data);
        
        // If payment is completed, refresh user profile to get updated subscription
        if (response.data.status === 'completed' && fetchUserProfile) {
          await fetchUserProfile();
        }

        // Clear stored order_id
        localStorage.removeItem('payhere_order_id');

        // Redirect to dashboard after 5 seconds if successful
        if (response.data.status === 'completed') {
          setTimeout(() => {
            navigate('/dashboard');
          }, 5000);
        } else {
          // For pending/failed, redirect after 8 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 8000);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        setError(err.response?.data?.error || 'Failed to verify payment status. Please check your dashboard.');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [navigate, fetchUserProfile]);

  if (loading) {
    return (
      <div className="payment-status-container">
        <div className="status-card">
          <FaSpinner className="spinner-icon" />
          <h1>Verifying Payment...</h1>
          <p>Please wait while we verify your payment status.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-status-container error">
        <div className="status-card">
          <FaTimesCircle className="error-icon" />
          <h1>Payment Verification Error</h1>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-primary mt-4">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getStatusDisplay = () => {
    if (!paymentStatus) return null;

    switch (paymentStatus.status) {
      case 'completed':
        return {
          icon: <FaCheckCircle className="success-icon" />,
          title: 'Payment Successful!',
          message: 'Your premium subscription has been activated.',
          color: 'success'
        };
      case 'pending':
        return {
          icon: <FaSpinner className="spinner-icon" />,
          title: 'Payment Pending',
          message: 'Your payment is being processed. Please wait a few moments.',
          color: 'pending'
        };
      case 'failed':
      case 'cancelled':
        return {
          icon: <FaTimesCircle className="error-icon" />,
          title: paymentStatus.status === 'cancelled' ? 'Payment Cancelled' : 'Payment Failed',
          message: 'Your payment could not be processed. Please try again.',
          color: 'error'
        };
      default:
        return {
          icon: <FaSpinner className="spinner-icon" />,
          title: 'Processing...',
          message: 'Your payment is being processed.',
          color: 'pending'
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className={`payment-status-container ${statusDisplay.color}`}>
      <div className="status-card">
        {statusDisplay.icon}
        <h1>{statusDisplay.title}</h1>
        <p>{statusDisplay.message}</p>
        {paymentStatus && (
          <>
            <p className="order-id">Order ID: {paymentStatus.orderId}</p>
            {paymentStatus.amount && (
              <p className="amount">Amount: ${paymentStatus.amount.toFixed(2)}</p>
            )}
          </>
        )}
        <p className="redirect-message">Redirecting to dashboard...</p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
